import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Drawer,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListSubheader,
  IconButton,
  Chip,
  Alert,
  Link,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import type { Expense } from '../../../types/expense';
import type { Supplier } from '../../../types/supplier';
import type { CategoryOption, KhaataEntryType } from '../../../constants/khaataCategories';
import {
  DEFAULT_CATEGORY_VALUE,
  TYPE_FIELD_CONFIG,
  buildCategoryOptions,
  getCategoriesGroupedForEntry,
  getTypeForCategory,
  getTypeLabel,
  formatCategoryLabel,
  isCategoryEnabled,
  getEnabledCategoriesForEntry,
} from '../../../constants/khaataCategories';
import { connect } from 'react-redux';
import KhaataBillUpload from './KhaataBillUpload';
import { getSuppliers } from '@utilities/supplierService';
import {
  renderSupplierMenuItem,
  getSupplierSelectLabel,
  khaataSelectMenuProps,
} from './SupplierSelectOption';

interface ExpenseDrawerProps {
  open: boolean;
  userId: string;
  restId: string;
  userName: string;
  expense: Expense | null;
  suppliers?: Supplier[];
  categoryOptions?: CategoryOption[];
  onClose: () => void;
  onSave: (expense: any) => void | Promise<void>;
  onManageSuppliers?: () => void;
  onManageCategories?: () => void;
}

type FormData = {
  date: string;
  category: string;
  description: string;
  amount: number;
  paymentMethod: Expense['paymentMethod'];
  billUrl?: string;
  supplierId?: string;
  supplierName?: string;
  invoiceNumber?: string;
  dueDate?: string;
};

const defaultFormData = (): FormData => ({
  date: new Date().toISOString().split('T')[0],
  category: DEFAULT_CATEGORY_VALUE,
  description: '',
  amount: 0,
  paymentMethod: 'pending',
  billUrl: undefined,
});

const TYPE_CHIP_COLOR: Record<KhaataEntryType, 'primary' | 'secondary' | 'info' | 'warning'> = {
  expense: 'primary',
  purchase: 'secondary',
  asset: 'info',
  liability: 'warning',
};

const ExpenseDrawer: React.FC<ExpenseDrawerProps> = ({
  open,
  expense,
  onClose,
  onSave,
  userId,
  restId,
  userName,
  suppliers: suppliersProp = [],
  categoryOptions: categoryOptionsProp,
  onManageSuppliers,
  onManageCategories,
}) => {
  const [formData, setFormData] = useState<FormData>(defaultFormData());
  const [isSaving, setIsSaving] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [localSuppliers, setLocalSuppliers] = useState<Supplier[]>([]);
  const [suppliersLoading, setSuppliersLoading] = useState(false);

  const suppliers = suppliersProp.length > 0 ? suppliersProp : localSuppliers;

  const categoryOptions = useMemo(
    () => buildCategoryOptions(categoryOptionsProp),
    [categoryOptionsProp]
  );

  const categoryGroups = useMemo(
    () => getCategoriesGroupedForEntry(categoryOptions, formData.category),
    [categoryOptions, formData.category]
  );

  const defaultCategoryForNewEntry = useMemo(() => {
    const enabled = getEnabledCategoriesForEntry(categoryOptions);
    const preferred = enabled.find((c) => c.value === DEFAULT_CATEGORY_VALUE);
    return preferred?.value || enabled[0]?.value || DEFAULT_CATEGORY_VALUE;
  }, [categoryOptions]);

  const entryType = useMemo(
    () => getTypeForCategory(formData.category, categoryOptions),
    [formData.category, categoryOptions]
  );
  const fieldConfig = TYPE_FIELD_CONFIG[entryType];

  useEffect(() => {
    if (!open) {
      setIsSaving(false);
      setIsImageUploading(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !restId || suppliersProp.length > 0) return;
    let cancelled = false;
    const load = async () => {
      setSuppliersLoading(true);
      try {
        const data = await getSuppliers(restId);
        if (!cancelled) setLocalSuppliers(data);
      } catch {
        if (!cancelled) setLocalSuppliers([]);
      } finally {
        if (!cancelled) setSuppliersLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [open, restId, suppliersProp.length]);

  useEffect(() => {
    if (!open) return;
    if (expense) {
      setFormData({
        date: expense.date,
        category: expense.category,
        description: expense.description,
        amount: expense.amount,
        paymentMethod: expense.paymentMethod,
        billUrl: expense.billUrl,
        supplierId: expense.supplierId,
        supplierName: expense.supplierName,
        invoiceNumber: expense.invoiceNumber || '',
        dueDate: expense.dueDate || '',
      });
    } else {
      setFormData({
        ...defaultFormData(),
        category: defaultCategoryForNewEntry,
      });
    }
  }, [expense, open, defaultCategoryForNewEntry]);

  const handleCategoryChange = useCallback(
    (value: string) => {
      const nextType = getTypeForCategory(value, categoryOptions);
      const nextConfig = TYPE_FIELD_CONFIG[nextType];
      setFormData((prev) => ({
        ...prev,
        category: value,
        supplierId: nextConfig.supplier ? prev.supplierId : undefined,
        supplierName: nextConfig.supplier ? prev.supplierName : undefined,
        invoiceNumber: nextConfig.invoice ? prev.invoiceNumber : undefined,
        dueDate: nextConfig.dueDate ? prev.dueDate : undefined,
      }));
    },
    [categoryOptions]
  );

  const handleSupplierChange = useCallback(
    (supplierId: string) => {
      const supplier = suppliers.find((s) => s.id === supplierId);
      setFormData((prev) => ({
        ...prev,
        supplierId,
        supplierName: supplier?.name || '',
      }));
    },
    [suppliers]
  );

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSaving || isImageUploading) return;
    if (fieldConfig.supplier && !formData.supplierId) return;

    setIsSaving(true);
    try {
      await onSave({
        id: expense?.id || '',
        userId,
        restId,
        userName,
        type: entryType,
        categoryLabel: formatCategoryLabel(formData.category, categoryOptions),
        ...formData,
        billUrl: formData.billUrl || undefined,
        supplierId: fieldConfig.supplier ? formData.supplierId : undefined,
        supplierName: fieldConfig.supplier ? formData.supplierName : undefined,
        invoiceNumber: fieldConfig.invoice ? formData.invoiceNumber?.trim() || undefined : undefined,
        dueDate: fieldConfig.dueDate ? formData.dueDate || undefined : undefined,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const isSubmitDisabled =
    isSaving || isImageUploading || (fieldConfig.supplier && !formData.supplierId);

  const expenseOrUserId = expense?.id || userId || 'noid';
  const drawerTitle = expense ? 'Edit Entry' : 'Add Entry';

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: { xs: '100%', sm: 420 } },
      }}
    >
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">{drawerTitle}</Typography>
            <Chip
              size="small"
              color={TYPE_CHIP_COLOR[entryType]}
              label={getTypeLabel(entryType)}
            />
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box component="form" onSubmit={handleSubmit}>
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="khaata-category-label">Category</InputLabel>
            <Select
              {...khaataSelectMenuProps}
              labelId="khaata-category-label"
              value={formData.category}
              label="Category"
              onChange={(e) => handleCategoryChange(e.target.value)}
              MenuProps={{
                ...khaataSelectMenuProps.MenuProps,
                PaperProps: {
                  ...khaataSelectMenuProps.MenuProps.PaperProps,
                  sx: {
                    ...khaataSelectMenuProps.MenuProps.PaperProps.sx,
                    maxHeight: 360,
                  },
                },
              }}
            >
              {categoryGroups.map((group) => [
                <ListSubheader
                  key={`header-${group.type}`}
                  sx={{
                    fontWeight: 700,
                    fontSize: '0.8125rem',
                    lineHeight: '32px',
                    bgcolor: 'grey.100',
                    color: 'text.primary',
                  }}
                >
                  {group.label}
                </ListSubheader>,
                ...group.categories.map((category) => {
                  const disabledInList = !isCategoryEnabled(category);
                  return (
                    <MenuItem
                      key={category.value}
                      value={category.value}
                      sx={{
                        pl: 3,
                        fontSize: '0.875rem',
                        ...(disabledInList && {
                          fontStyle: 'italic',
                          color: 'text.secondary',
                        }),
                      }}
                    >
                      {category.label}
                      {disabledInList ? ' (disabled)' : ''}
                    </MenuItem>
                  );
                }),
              ])}
            </Select>
          </FormControl>

          {onManageCategories && (
            <Box sx={{ mt: 0.5, mb: 0.5 }}>
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={onManageCategories}
                sx={{ fontWeight: 600 }}
              >
                Manage Categories
              </Link>
            </Box>
          )}

          <TextField
            fullWidth
            label="Date"
            type="date"
            value={formData.date}
            onChange={(e) => setFormData((prev) => ({ ...prev, date: e.target.value }))}
            margin="normal"
            required
            InputLabelProps={{ shrink: true }}
          />

          {fieldConfig.supplier && (
            <>
              <FormControl fullWidth margin="normal" required error={!formData.supplierId}>
                <InputLabel id="khaata-drawer-supplier-label">Supplier</InputLabel>
                <Select
                  {...khaataSelectMenuProps}
                  labelId="khaata-drawer-supplier-label"
                  value={formData.supplierId || ''}
                  label="Supplier"
                  onChange={(e) => handleSupplierChange(e.target.value)}
                  disabled={suppliersLoading}
                  displayEmpty
                  renderValue={(selected) => {
                    if (!selected) return '';
                    return getSupplierSelectLabel(suppliers, selected as string);
                  }}
                >
                  {suppliers.length === 0
                    ? [
                        <MenuItem key="empty" value="" disabled>
                          No suppliers — add one first
                        </MenuItem>,
                      ]
                    : suppliers.map((supplier) => renderSupplierMenuItem(supplier))}
                </Select>
              </FormControl>
              {suppliers.length === 0 && onManageSuppliers && (
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Link
                    component="button"
                    type="button"
                    variant="body2"
                    onClick={onManageSuppliers}
                    sx={{ fontWeight: 600 }}
                  >
                    Manage Suppliers
                  </Link>
                  {' '}to add suppliers.
                </Alert>
              )}
            </>
          )}

          {fieldConfig.invoice && (
            <TextField
              fullWidth
              label="Invoice / Bill Number"
              value={formData.invoiceNumber || ''}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, invoiceNumber: e.target.value }))
              }
              margin="normal"
            />
          )}

          {fieldConfig.dueDate && (
            <TextField
              fullWidth
              label="Payment Due Date (optional)"
              type="date"
              value={formData.dueDate || ''}
              onChange={(e) => setFormData((prev) => ({ ...prev, dueDate: e.target.value }))}
              margin="normal"
              InputLabelProps={{ shrink: true }}
              helperText="For credit / pending payments"
            />
          )}

          <TextField
            fullWidth
            label="Description (optional)"
            value={formData.description}
            onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
            margin="normal"
            multiline
            rows={3}
          />

          <TextField
            fullWidth
            label="Amount"
            type="number"
            value={formData.amount}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, amount: Number(e.target.value) }))
            }
            margin="normal"
            required
            inputProps={{ min: 0, step: '0.01' }}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₹</Typography>,
            }}
          />

          <FormControl fullWidth margin="normal" required>
            <InputLabel>Payment Method</InputLabel>
            <Select
              {...khaataSelectMenuProps}
              value={formData.paymentMethod}
              label="Payment Method"
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  paymentMethod: e.target.value as Expense['paymentMethod'],
                }))
              }
            >
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="pending">Pending</MenuItem>
            </Select>
          </FormControl>

          {restId && (
            <Box sx={{ mt: 2 }}>
              <KhaataBillUpload
                onFileUpload={(url) => setFormData((prev) => ({ ...prev, billUrl: url }))}
                onFileRemove={() => setFormData((prev) => ({ ...prev, billUrl: undefined }))}
                onUploadingChange={setIsImageUploading}
                restaurantId={restId}
                expenseOrUserId={expenseOrUserId}
                existingUrl={formData.billUrl}
                maxFileSizeMB={5}
                disabled={isSaving}
              />
            </Box>
          )}

          <Button
            fullWidth
            variant="contained"
            type="submit"
            disabled={isSubmitDisabled}
            sx={{ mt: 3 }}
          >
            {isSaving
              ? expense
                ? 'Updating…'
                : 'Adding…'
              : isImageUploading
                ? 'Uploading bill…'
                : expense
                  ? 'Update Entry'
                  : 'Add Entry'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
};

const mapStateToProps = (state: any) => {
  const { common, user } = state;
  return {
    userId: user?.user?.uid,
    userName: user?.user?.name,
    restId: common?.selectedRestaurant?.docId || '',
  };
};

export default connect(mapStateToProps)(ExpenseDrawer);
